/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useContext, useEffect, useLayoutEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { Button } from '@marriott/mi-ui-library';
import { internetBarIcons, statesConstant } from '../../../../modules/utils/constants';
import { PageParamsContext } from '../../../../modules/context/PageContext';
import { serviceStore, useServiceStore } from '../../../../modules/store/serviceStore';
import { zoneValues } from '../../../../modules/utils/constants';
import { INTERNET_BAR_INITIAL_STATE } from '../../utils/constant';
import { inStayMockResponse } from '../../__mock__/InternetBar.model';
import { AEMInternetBarProps, InternetBarExperienceProps, InternetBarStateTypes } from '../../InternetBar.types';
import {
  analyticsTrackingIdentifier,
  analyticsTrackingUniqueDescription,
  getInternetBarTrackingIdentifier,
} from '../../../../modules/utils/constants/constants';
import { getInternetBarStateSpecificClasses, StateCSS } from '../../internetBar.helper';
import {
  connectBtnTrigger,
  getLoyaltyEnabledForConferenceZone,
  getSFRoomLastNameValidationFlag,
  isMBVMember,
} from '../../utils/handler';
import { updateDataLayerProperties, sanitize } from '../../../../modules/utils/common';
import { useSession } from '@marriott/mi-headless-utils';

export const InternetBarPreAgeExperience: FC<InternetBarExperienceProps> = (pageProps: InternetBarExperienceProps) => {
  const router = useRouter();
  const urlQueryParams: Record<string, any> = router.query;
  const { Z = '', AT = '' } = urlQueryParams;
  const serviceData = useServiceStore((state: serviceStore) => state.serviceData);

  const { preProcessorResponse } = useContext(PageParamsContext);
  const {
    experienceSegment = '',
    state = '',
    userSegment = '',
    userState = '',
    enableLoyalty = false as boolean,
    cna = '',
    sameTab = '',
    enableLoyaltyPostConnect = false as boolean,
    enableLoyaltyConferencePostConnect = false as boolean,
    enableLoyaltyConference = false as boolean,
    enrollmentSourceCodeMapping,
    propertyInfo,
  } = preProcessorResponse || {};

  const { isACDLenabled } = useSession();
  const isLoyaltyEnabledForConference = getLoyaltyEnabledForConferenceZone(
    enableLoyaltyConference ? enrollmentSourceCodeMapping?.loyalty : enrollmentSourceCodeMapping?.nonloyalty,
    propertyInfo?.data?.basicInformation?.globalDivision,
    propertyInfo?.data?.contactInformation?.address?.country?.code
  );

  let currentStateMapping = pageProps?.isAuthorMode
    ? { ...inStayMockResponse }
    : ({ ...INTERNET_BAR_INITIAL_STATE } as InternetBarStateTypes);

  currentStateMapping = {
    ...pageProps?.currentStateMapping,
  };

  const termsAndConditionsRef = useRef<HTMLParagraphElement>(null);

  const [showIcon] = useState<boolean>(
    (state?.toString()?.toLowerCase() === statesConstant.connect ||
      state?.toString()?.toLowerCase() === statesConstant.preconnect) &&
      internetBarIcons &&
      internetBarIcons?.[state]
      ? internetBarIcons?.[state]?.showIcon
      : false
  );
  const [isMBVUser, setIsMBVState] = useState<boolean>(false);
  const [descriptionText, setDescriptionText] = useState<string | TrustedHTML>('');
  const [showButtonContainer, setShowButtonContainer] = useState<boolean>(false);
  const [isJoinNowBtnLoading, setIsJoinNowBtnLoading] = useState<boolean>(pageProps?.isAuthorMode ? false : true);
  const [isDefaultConnectNowBtnLoading, setDefaultConnectNowBtnLoading] = useState<boolean>(false);
  const [defaultJoinNow, setDefaultJoinNow] = useState<string>(
    pageProps?.[currentStateMapping?.joinNowButton?.joinButtonLink as keyof AEMInternetBarProps] as string
  );

  const stateCSS: StateCSS = getInternetBarStateSpecificClasses(userSegment, state, experienceSegment, enableLoyalty);

  // Get the HTML content sanitized for below HTML
  const welcomeText = sanitize(
    (pageProps?.[currentStateMapping?.welcomeText as keyof AEMInternetBarProps] || '').toString().replace(/<\/?p>/g, '')
  );

  const signInText = sanitize(
    (
      currentStateMapping?.signInText && pageProps?.[currentStateMapping?.signInText as keyof AEMInternetBarProps]
    )?.toString() || ''
  );

  const termsAndConditionsText = sanitize(
    (
      currentStateMapping?.termsAndConditions &&
      pageProps?.[currentStateMapping?.termsAndConditions as keyof AEMInternetBarProps]
    )?.toString() || ''
  );

  const [v5Redirect, setV5Redirect] = useState<boolean>(urlQueryParams?.['v5Redirect'] === 'true' || false);

  const isWelcomeTitle: boolean = currentStateMapping && currentStateMapping?.title === 'welcomeTitle';

  useEffect(() => {
    if (pageProps && pageProps?.internetPricingData && pageProps?.internetPricingData?.data) {
      const { enrollmentSourceCode = '', userExperience = '' } = pageProps?.internetPricingData?.data ?? {};
      if (!isACDLenabled) {
        const dataLayer = (window && window?.dataLayer) ?? {};
        updateDataLayerProperties(dataLayer, userExperience, enrollmentSourceCode);
      }
    }
  }, [pageProps?.internetPricingData?.data, isACDLenabled]);

  useEffect(() => {
    if (termsAndConditionsRef && termsAndConditionsRef?.current) {
      const { Z = '', zoneMapping } = pageProps?.internetBarCurrentStateData ?? {};
      const propertyPricingTier = zoneMapping?.[Z ?? 'GUEST'];
      const termsAndConditionsLinks = termsAndConditionsRef.current.getElementsByTagName('a');
      if (termsAndConditionsLinks.length > 0) {
        const offersTermsConditionLink = termsAndConditionsLinks[0];
        offersTermsConditionLink.classList.add('custom_click_track');
        offersTermsConditionLink?.setAttribute(
          'data-custom_click_track_value',
          `${getInternetBarTrackingIdentifier(state, analyticsTrackingIdentifier?.internetBar, propertyPricingTier)}|${
            analyticsTrackingUniqueDescription.termsOfUse
          }|internal|`
        );
      }
    }
  }, [termsAndConditionsText, currentStateMapping, pageProps?.internetBarCurrentStateData]);

  const getConnectUpgradeBtn = () => {
    const { ACTION, Z, zoneMapping, U_URLEndpoint } = pageProps?.internetBarCurrentStateData ?? {};
    const property = zoneMapping?.['GUEST'];
    const propertyPricingTier = zoneMapping?.[Z ?? 'GUEST'];
    if (
      !currentStateMapping?.isConnectState &&
      (!currentStateMapping?.showUpgradeModal || state === statesConstant?.connected) &&
      !(ACTION === 'CONNECTED' && Z === 'GUEST' && U_URLEndpoint !== '' && (property === 'FP' || property === 'PP'))
    ) {
      return (
        <Button
          className={clsx('m-button-primary', `${stateCSS?.internetBarConnectButton}`)}
          isLink={true}
          href={pageProps?.upgradeButtonLink}
          trackingProperties={pageProps?.trackingProperties}
          target={sameTab || cna ? '_self' : '_blank'}
          custom_click_track_value={`${getInternetBarTrackingIdentifier(
            state,
            analyticsTrackingIdentifier.internetBar,
            propertyPricingTier
          )}|${currentStateMapping?.isConnectState ? 'Connect Link' : 'Upgrade Link'}|internal`}
          data-custom_click_track_value={`${getInternetBarTrackingIdentifier(
            state,
            analyticsTrackingIdentifier.internetBar,
            propertyPricingTier
          )}|${currentStateMapping?.isConnectState ? 'Connect Link' : 'Upgrade Link'}|internal`}
          data-is-click-track={true}
          ariaLabel={
            ((currentStateMapping?.connectUpgradeBtn &&
              pageProps?.[currentStateMapping?.connectUpgradeBtn as keyof AEMInternetBarProps]) ||
              pageProps?.connectButtonLabel) as string
          }
        >
          <span data-testid="internet-bar-upgrade-link">
            {(currentStateMapping?.connectUpgradeBtn &&
              pageProps?.[currentStateMapping?.connectUpgradeBtn as keyof AEMInternetBarProps]) ||
              pageProps?.connectButtonLabel}
          </span>
        </Button>
      );
    } else {
      return (
        <Button
          className={clsx(
            'm-button-primary',
            `${stateCSS?.internetBarConnectButton}`,
            `${isDefaultConnectNowBtnLoading && currentStateMapping?.isConnectState ? 'disabled' : ''}`
          )}
          isDisabled={isDefaultConnectNowBtnLoading && currentStateMapping?.isConnectState}
          callback={() => {
            currentStateMapping?.isConnectState ? handleBtnClick('connect') : handleBtnClick('upgrade');
          }}
          trackingProperties={pageProps?.trackingProperties}
          custom_click_track_value={`${getInternetBarTrackingIdentifier(
            state,
            analyticsTrackingIdentifier.internetBar,
            propertyPricingTier
          )}|${currentStateMapping?.isConnectState ? 'Connect Button ' : 'Upgrade Button'}|internal`}
          data-custom_click_track_value={`${getInternetBarTrackingIdentifier(
            state,
            analyticsTrackingIdentifier.internetBar,
            propertyPricingTier
          )}|${currentStateMapping?.isConnectState ? 'Connect Button' : 'Upgrade Button'}|internal`}
          data-is-click-track={true}
          ariaLabel={
            ((currentStateMapping?.connectUpgradeBtn &&
              pageProps?.[currentStateMapping?.connectUpgradeBtn as keyof AEMInternetBarProps]) ||
              pageProps?.connectButtonLabel) as string
          }
        >
          {isDefaultConnectNowBtnLoading ? (
            <div className="m-spinner-s" data-testid="activate-spinner"></div>
          ) : (
            <span data-testid="internet-bar-connect-upgrade-btn">
              {(currentStateMapping?.connectUpgradeBtn &&
                pageProps?.[currentStateMapping?.connectUpgradeBtn as keyof AEMInternetBarProps]) ||
                pageProps?.connectButtonLabel}
            </span>
          )}
        </Button>
      );
    }
  };

  const handleBtnClick = (btnName: string) => {
    const internetbarInitialValue = { ...pageProps?.internetBarCurrentStateData };
    const zoneValue = internetbarInitialValue['Z'] ?? '';
    const localRequirement = internetbarInitialValue['LR'] ?? '';
    const U_URLEndpoint = internetbarInitialValue['U_URLEndpoint'] ?? '';
    const ACTION = internetbarInitialValue['ACTION'] ?? '';
    const property = internetbarInitialValue['zoneMapping']?.['GUEST'] ?? '';
    const { setCurrentStateHasModalOption, setModalType, setShowInternetBarModal } = pageProps;

    switch (btnName) {
      case 'connect':
        setDefaultConnectNowBtnLoading(currentStateMapping?.noLoginInterfaceModal ?? false ? true : false);
        setShowInternetBarModal(currentStateMapping?.noLoginInterfaceModal ?? false ? false : true);
        if (localRequirement?.toUpperCase() === 'AF_AO' && zoneValue !== 'GUEST') {
          setCurrentStateHasModalOption(!currentStateMapping?.noLoginInterfaceModal);
          setModalType('ACCESS');
          connectBtnTrigger(currentStateMapping?.noLoginInterfaceModal ?? false, { ...internetbarInitialValue });
        } else if (localRequirement?.toUpperCase() === 'STRICT_AO') {
          setCurrentStateHasModalOption(!currentStateMapping?.noLoginInterfaceModal);
          setModalType('ACCESS');
          connectBtnTrigger(currentStateMapping?.noLoginInterfaceModal ?? false, { ...internetbarInitialValue });
        } else {
          const { Z = '', zoneMapping } = pageProps?.internetBarCurrentStateData || {};
          const isSFRoomLastNameValidationFlag = getSFRoomLastNameValidationFlag(
            preProcessorResponse &&
              (enableLoyalty ||
              enableLoyaltyPostConnect ||
              ((enableLoyaltyConference || enableLoyaltyConferencePostConnect) && isLoyaltyEnabledForConference)
                ? enrollmentSourceCodeMapping?.loyalty
                : enrollmentSourceCodeMapping?.nonloyalty),
            propertyInfo?.data?.basicInformation?.globalDivision,
            propertyInfo?.data?.contactInformation?.address?.country?.code
          );
          if (!isSFRoomLastNameValidationFlag && !localRequirement && zoneMapping?.[Z ?? ''] === 'SF') {
            connectBtnTrigger(true, { ...internetbarInitialValue });
          } else {
            setModalType('LOGIN');
            connectBtnTrigger(currentStateMapping?.noLoginInterfaceModal ?? false, { ...internetbarInitialValue });
          }
        }

        break;

      case 'upgrade':
        if (currentStateMapping?.showUpgradeModal) {
          setShowInternetBarModal(true);
          currentStateMapping?.showLoginModal ? setModalType('LOGIN') : setModalType('U_PRICE');
          setCurrentStateHasModalOption(true);
        } else if (
          ACTION === 'CONNECTED' &&
          zoneValue?.toUpperCase() === 'GUEST' &&
          U_URLEndpoint !== '' &&
          (property === 'FP' || property === 'PP')
        ) {
          setShowInternetBarModal(true);
          setModalType('UPGRADE_LOGIN');
          setCurrentStateHasModalOption(true);
        }
        break;
    }
  };

  useEffect(() => {
    if (
      pageProps &&
      pageProps?.internetPricingData &&
      (pageProps?.internetPricingData?.data || pageProps?.internetPricingData?.error)
    ) {
      setIsJoinNowBtnLoading(false);
    }
  }, [pageProps]);

  useEffect(() => {
    if (urlQueryParams && urlQueryParams?.['v5Redirect'] === 'true') {
      setV5Redirect(true);
    }
  }, [urlQueryParams]);

  useEffect(() => {
    if (isMBVMember(userState)) {
      setIsMBVState(true);
    }
  }, [userState]);

  // OSD-9414 - Conference Zone verbiage update for pre-connect-logged-in scenarios and non-loggedin scenarios
  useEffect(() => {
    const isSignInBtnAvailable =
      currentStateMapping && currentStateMapping?.signInButton && currentStateMapping?.signInButton?.signInButtonLabel
        ? true
        : false;

    let showBtnContainer = false;
    const showBtnContainerWithSignInBtn = !!(
      currentStateMapping &&
      (currentStateMapping?.joinNowButton?.joinButtonLabel ||
        currentStateMapping?.signInButton?.signInButtonLabel ||
        currentStateMapping?.connectUpgradeBtn)
    );

    const showBtnContainerWithoutSignInBtn = !!(
      pageProps?.internetSignInText &&
      currentStateMapping &&
      (currentStateMapping?.joinNowButton?.joinButtonLabel || currentStateMapping?.connectUpgradeBtn)
    );
    //  pre-connect logged-in
    if (isMBVMember(userState)) {
      setIsMBVState(true);
      let conferenceZoneMemberDescriptionValue: string = '';

      if (
        (Z?.toString()?.toLowerCase() === zoneValues.CONFERENCE ||
          Z?.toString()?.toLowerCase() === zoneValues.MEETING) &&
        (AT?.toString()?.toLowerCase() === statesConstant.connect ||
          AT?.toString()?.toLowerCase() === statesConstant.preconnect) &&
        // The updated conferenceZoneMemberDescriptionValue is not applicable for remember-me scenarios
        !preProcessorResponse?.experienceSegment?.toLowerCase()?.includes('remember')
      ) {
        switch (userState?.toLowerCase()) {
          case 'basic':
            conferenceZoneMemberDescriptionValue =
              (pageProps?.conferenceZoneBasicDescription
                ? pageProps?.conferenceZoneBasicDescription
                : (pageProps?.[currentStateMapping?.description as keyof AEMInternetBarProps] as string)) ?? '';
            break;

          case 'elite':
            conferenceZoneMemberDescriptionValue =
              (pageProps?.conferenceZoneEliteDescription
                ? pageProps?.conferenceZoneEliteDescription
                : (pageProps?.[currentStateMapping?.description as keyof AEMInternetBarProps] as string)) ?? '';
            break;

          default:
            break;
        }
      } else {
        conferenceZoneMemberDescriptionValue =
          (pageProps?.[currentStateMapping?.description as keyof AEMInternetBarProps] as string) ?? '';
      }
      showBtnContainer = showBtnContainerWithSignInBtn;

      if (pageProps?.internetSignInText && isSignInBtnAvailable && userSegment !== 'Logged In') {
        conferenceZoneMemberDescriptionValue = conferenceZoneMemberDescriptionValue
          ? `${conferenceZoneMemberDescriptionValue?.trim()} ${pageProps?.internetSignInText}`
          : `${pageProps?.internetSignInText}`;

        showBtnContainer = showBtnContainerWithoutSignInBtn;
      }

      const description = sanitize(`${conferenceZoneMemberDescriptionValue}`?.toString()?.replace(/<\/?p>/g, ''));
      setDescriptionText(description);
    } else {
      // non-logged in scenarios
      const conferenceZoneDescriptionValue =
        (Z?.toString()?.toLowerCase() === zoneValues.CONFERENCE ||
          Z?.toString()?.toLowerCase() === zoneValues.MEETING) &&
        pageProps?.conferenceZoneUnknownDescription
          ? pageProps?.conferenceZoneUnknownDescription
          : pageProps[currentStateMapping?.description as keyof AEMInternetBarProps];

      const description = sanitize((conferenceZoneDescriptionValue || '')?.toString()?.replace(/<\/?p>/g, ''));
      setDescriptionText(description);

      showBtnContainer = showBtnContainerWithSignInBtn;
    }
    setShowButtonContainer(showBtnContainer);
  }, [preProcessorResponse]);

  useEffect(() => {
    if (serviceData?.connectBtnClick) {
      handleBtnClick('connect');
    }
  }, [serviceData?.connectBtnClick]);

  useEffect(() => {
    if (
      pageProps &&
      pageProps?.internetPricingData &&
      pageProps?.internetPricingData?.data &&
      pageProps?.internetPricingData?.data?.enrollmentSourceCode
    ) {
      const { enrollmentSourceCode } = pageProps?.internetPricingData?.data ?? {};
      const { Z, zoneMapping } = pageProps?.internetBarCurrentStateData ?? {};
      if (enableLoyaltyPostConnect && enrollmentSourceCode) {
        const joinNowLink = pageProps?.handleLinks(enrollmentSourceCode, zoneMapping, Z);
        setDefaultJoinNow(joinNowLink);
      } else if ((enableLoyaltyConference || enableLoyaltyConferencePostConnect) && isLoyaltyEnabledForConference) {
        const joinNowLink = pageProps?.handleLinks(
          pageProps?.internetBarCurrentStateData?.['loyaltyEnrollmentSourceCode'],
          zoneMapping,
          Z
        );

        setDefaultJoinNow(joinNowLink);
      } else {
        const defaultEnrollmentSourceCode = pageProps?.internetPricingData?.['data']?.enrollmentSourceCode
          ? pageProps?.internetPricingData?.['data']?.enrollmentSourceCode
          : pageProps?.internetBarCurrentStateData['enrollmentSourceCode'];
        const joinNowLink = pageProps?.handleLinks(defaultEnrollmentSourceCode, zoneMapping, Z);
        setDefaultJoinNow(joinNowLink);
      }
    }
  }, [
    pageProps?.internetPricingData?.data,
    pageProps,
    enableLoyaltyPostConnect,
    enableLoyaltyConference,
    enableLoyaltyConferencePostConnect,
    enrollmentSourceCodeMapping?.loyalty,
    enrollmentSourceCodeMapping?.nonloyalty,
    propertyInfo?.data?.basicInformation?.globalDivision,
    propertyInfo?.data?.contactInformation?.address.country.code,
    isLoyaltyEnabledForConference,
  ]);

  useLayoutEffect(() => {
    pageProps?.resizeImage && pageProps?.resizeImage();
  }, []);

  return currentStateMapping && pageProps && descriptionText ? (
    <div
      className={clsx(
        `container internet-bar-content ${
          (enableLoyalty || enableLoyaltyConference) && isLoyaltyEnabledForConference
            ? `internet-bar-content-${state}-loyalty`
            : `internet-bar-content-${state}`
        }`,
        `${stateCSS?.internetBarContent}`
      )}
    >
      <div
        className={clsx(
          'col-12 d-flex flex-column justify-content-center px-0 text-center align-items-center',
          `${stateCSS?.internetBarContentContainer}`
        )}
      >
        {/* 1. Internet Bar Icon Configuration */}
        {showIcon && (
          <span
            className={clsx(
              `t-accent-color d-inline-block ${internetBarIcons?.[state]?.iconName} internet-bar-content__icon internet-bar-content-${state}__icon`,
              `${stateCSS?.internetBarIcon}`
            )}
            data-testid="internet-bar-icon"
          ></span>
        )}
        {/* 2. Title */}
        {isWelcomeTitle ? (
          <>
            <h3
              className={clsx(
                `mt-3 mb-3 pt-xl-4 internet-bar-content__title internet-bar-content-${state}__title`,
                `${showIcon ? '' : 'pt-2 pt-md-4'}`
              )}
              data-testid="internet-bar-welcome-title"
            >
              {v5Redirect
                ? pageProps?.tier1Title
                : pageProps?.[currentStateMapping?.title as keyof AEMInternetBarProps] || ''}
            </h3>
            {/* Separator */}
            <div
              className={`t-accent-color mb-3 mb-xl-4 internet-bar-content__separator internet-bar-content-${state}__separator`}
            ></div>
          </>
        ) : (
          <h3
            className={clsx(
              'col-12 internet-bar-content__title',
              `${stateCSS?.internetBarNotWelcomeTitle}`,
              `${showIcon ? '' : 'pt-4 pt-xl-4'}`
            )}
            data-testid="internet-bar-welcome-title"
          >
            {pageProps?.[currentStateMapping?.title as keyof AEMInternetBarProps] ?? ''}
          </h3>
        )}

        <div
          className={clsx(
            `col-12 pr-0 pl-0 t-font-s internet-bar-content__description-container internet-bar-content-${state}__description-container`,
            `${stateCSS?.internetBarContentDescriptionContainer}`
          )}
        >
          {/* 3. Welcome Text */}
          {currentStateMapping?.welcomeText && (
            <p
              className={clsx(
                'internet-bar-content__description-container__description',
                `${stateCSS?.internetBarWelcomeText}`
              )}
              data-testid="internet-bar-welcome-text"
              dangerouslySetInnerHTML={{
                __html: welcomeText,
              }}
            ></p>
          )}
          {/* 4. Description */}
          {(currentStateMapping?.description || descriptionText) && (
            <p
              className={clsx(
                'internet-bar-content__description-container__description',
                `${stateCSS?.internetBarDescription}`
              )}
              data-testid="internet-bar-description"
              dangerouslySetInnerHTML={{
                __html: descriptionText,
              }}
            ></p>
          )}
        </div>
        {showButtonContainer ? (
          <div
            className={clsx(
              `col-12 internet-bar-content__button-container internet-bar-content-${state}__button-container`,
              `${stateCSS?.internetBarButtonContainer}`
            )}
          >
            <div
              className={clsx(
                `d-flex flex-column justify-content-center px-2 px-xl-0 internet-bar-content-${state}__button-content internet-bar-content__button-container-item`,
                `${stateCSS?.internetBarButtonContentContainer}`,
                `${
                  !(
                    state?.toString()?.toLowerCase() === statesConstant.connect ||
                    state?.toString()?.toLowerCase() === statesConstant.preconnect
                  )
                    ? ' flex-md-row justify-content-around'
                    : ' '
                }`
              )}
            >
              {/* 5. Sign In  - Primary Button  */}
              {((v5Redirect && !isMBVUser) || (!v5Redirect && !pageProps?.internetSignInText)) &&
                currentStateMapping?.signInButton?.signInButtonLabel &&
                currentStateMapping?.signInButton?.signInButtonLink && (
                  <Button
                    className={`internet-bar-content-${state}__button m-button  ${
                      stateCSS.internetBarSigninButton ? stateCSS.internetBarSigninButton : 'm-button-secondary'
                    }`}
                    custom_click_track_value={`${getInternetBarTrackingIdentifier(
                      state,
                      analyticsTrackingIdentifier.internetBar,
                      pageProps?.internetBarCurrentStateData?.['zoneMapping']?.[
                        pageProps?.internetBarCurrentStateData?.['Z'] ?? 'GUEST'
                      ]
                    )}|Sign In Button|internal`}
                    data-custom_click_track_value={`${getInternetBarTrackingIdentifier(
                      state,
                      analyticsTrackingIdentifier.internetBar,
                      pageProps?.internetBarCurrentStateData?.['zoneMapping']?.[
                        pageProps?.internetBarCurrentStateData?.['Z'] ?? 'GUEST'
                      ]
                    )}|Sign In Button|internal`}
                    data-is-click-track={true}
                    isLink={true}
                    target="_blank"
                    trackingProperties={pageProps?.trackingProperties}
                    href={(
                      pageProps?.[
                        currentStateMapping?.signInButton?.signInButtonLink as keyof AEMInternetBarProps
                      ] as string
                    )?.toString()}
                    ariaLabel={
                      (pageProps?.[currentStateMapping?.signInText as keyof AEMInternetBarProps] ||
                        pageProps?.signInButtonLabel) as string
                    }
                  >
                    <span data-testid="internet-bar-sign-in">
                      {pageProps?.[currentStateMapping?.signInText as keyof AEMInternetBarProps] ||
                        pageProps?.signInButtonLabel}
                    </span>
                  </Button>
                )}

              {/* 6. Join Now  - Primary / Secondary Button  */}
              {currentStateMapping?.joinNowButton?.joinButtonLabel &&
                currentStateMapping?.joinNowButton?.joinButtonLink && (
                  <Button
                    className={`m-button d-flex align-items-center justify-content-center mb-2 mb-md-4 ${
                      stateCSS.internetBarJoinNowButton ? stateCSS.internetBarJoinNowButton : 'm-button-primary'
                    } ${isJoinNowBtnLoading ? 'disable-join-now' : 'enable-join-now'}`}
                    isLink={!isJoinNowBtnLoading}
                    custom_click_track_value={`${getInternetBarTrackingIdentifier(
                      state,
                      analyticsTrackingIdentifier.internetBar,
                      pageProps?.internetBarCurrentStateData?.['zoneMapping']?.[
                        pageProps?.internetBarCurrentStateData?.['Z'] ?? 'GUEST'
                      ]
                    )}|Join Now Button|internal`}
                    data-custom_click_track_value={`${getInternetBarTrackingIdentifier(
                      state,
                      analyticsTrackingIdentifier.internetBar,
                      pageProps?.internetBarCurrentStateData?.['zoneMapping']?.[
                        pageProps?.internetBarCurrentStateData?.['Z'] ?? 'GUEST'
                      ]
                    )}|Join Now Button|internal`}
                    data-is-click-track={true}
                    href={defaultJoinNow}
                    target={
                      pageProps?.[
                        currentStateMapping?.joinNowButton?.joinButtonLinkInNewTab as keyof AEMInternetBarProps
                      ] as string
                    }
                    ariaLabel={
                      (pageProps?.[currentStateMapping?.joinNowButton?.joinButtonLabel as keyof AEMInternetBarProps] ||
                        pageProps?.joinButtonLabel) as string
                    }
                  >
                    <span data-testid="internet-bar-join-now">
                      {pageProps?.[currentStateMapping?.joinNowButton?.joinButtonLabel as keyof AEMInternetBarProps] ||
                        pageProps?.joinButtonLabel}
                    </span>
                  </Button>
                )}

              {/* 7. Connect / Upgrade - Primary Button  */}
              {currentStateMapping?.connectUpgradeBtn && getConnectUpgradeBtn()}
            </div>
          </div>
        ) : (
          <div className="mb-3" />
        )}
        <div
          className={clsx(
            `col-12 internet-bar-content__footer-container internet-bar-content-${state}__footer-container`,
            `${stateCSS?.internetBarFooterContainer}`
          )}
        >
          {/* 8. Terms and Condition Text  */}
          {currentStateMapping?.termsAndConditions && (
            <p
              ref={termsAndConditionsRef}
              className={clsx(
                `mb-0 t-font-xs internet-bar-content__footer-container__tnc-text`,
                `${stateCSS?.internetBarTermsAndConditions}`
              )}
              data-testid="internet-bar-terms-condition"
              dangerouslySetInnerHTML={{
                __html: termsAndConditionsText,
              }}
            ></p>
          )}
          {/* 9. Sign In text */}
          {currentStateMapping?.signInText && (
            <p
              className="t-font-xs pb-4 mb-1 mb-xl-0 internet-bar-content__footer-container__signin-text"
              dangerouslySetInnerHTML={{
                __html: signInText,
              }}
              data-testid="internet-bar-sign-in-text"
            ></p>
          )}
        </div>
      </div>
    </div>
  ) : null;
};
